<script lang="ts">
	import Icon from './Icon.svelte';
	import SocialSharing from './SocialSharing.svelte';
	import StageShapeContainer from './StageShapeContainer.svelte';
	import type { EventType } from '@api/EventsApi';
	import { isEvent, isWorkshop } from '$lib/event';
	import ContentClock from '@icons/ContentClock.svelte';
	import Person from '@icons/Person.svelte';
	import RightCondensed from '@icons/RightCondensed.svelte';
	import PriceCircle from '@icons/PriceCircle.svelte';
	import CalendarCircle from '@icons/CalendarCircle.svelte';
	import StageHeadline from './StageHeadline.svelte';
	import SeoHeader from './SeoHeader.svelte';

	export let eventName: string;
	export let eventType: EventType;
	export let imageSrc: string;
	export let imageAlt: string;
	export let durationMinutes: number = 0;
	export let fsk: number = 0;
	export let priceCents: number;
	export let metaTitle: string;
	export let metaDescription: string;
	export let usesCmsEventTicketsComponent: boolean;
</script>

<SeoHeader title={metaTitle} description={metaDescription} image={imageSrc} />

<div class="cms-detail-header h-96 md:h-[31.25rem] mx-auto mb-56 lg:mb-0" {...$$restProps}>
	<img src={imageSrc} alt={imageAlt} class="header-image" />
	<div class="absolute -bottom-52 lg:-bottom-12 w-full">
		<StageShapeContainer class="">
			<div
				class="text-white uppercase font-extrabold text-quaternary leading-none mb-1 md:mb-3 tracking-[0.25rem]"
				class:mt-6={eventName.length < 46}
			>
				{#if isWorkshop(eventType)}
					Workshop
				{/if}
				{#if isEvent(eventType)}
					Event
				{/if}
			</div>

			<StageHeadline text={eventName} />

			<ul
				class="flex gap-4 text-white text-quaternary font-bold bottom-10 absolute flex-wrap max-w-[70%]"
			>
				{#if durationMinutes > 0}
					<li class="inline-flex gap-1.5 items-center">
						<Icon component={ContentClock} aria-label="Dauer" />
						{durationMinutes} min
					</li>
				{/if}
				<li class="inline-flex gap-1.5 items-center">
					<Icon component={PriceCircle} class="!w-5.5 !h-5.5" aria-label="Kosten" />
					{priceCents > 0 ? `${priceCents / 100} €` : 'Kostenlos'}
				</li>
				{#if fsk > 0}
					<li class="inline-flex gap-1.5 items-center">
						<Icon component={Person} />
						Mindestalter: {fsk} Jahre
					</li>
				{/if}
			</ul>
		</StageShapeContainer>
	</div>
</div>

<div class="mt-6 relative flex px-3 z-10">
	<div
		class="flex flex-col lg:flex-row w-full justify-end gap-3 lg:gap-6 md:px-3 lg:px-6 overflow-visible"
	>
		<a class="flex gap-3 min-w-fit items-center focus:underline hover:underline" href="#details">
			<div class="w-7.5 h-7.5 rounded-full border border-gray-1 flex justify-center items-center">
				<Icon component={RightCondensed} />
			</div>
			Mehr Details
		</a>
		{#if usesCmsEventTicketsComponent}
			<a class="flex gap-3 min-w-fit items-center focus:underline hover:underline" href="#termine">
				<div class="w-7.5 h-7.5 rounded-full border border-gray-1 flex justify-center items-center">
					<Icon component={CalendarCircle} />
				</div>
				Zu den Terminen
			</a>
		{/if}
		<SocialSharing />
	</div>
</div>

<style lang="postcss">
	.cms-detail-header {
		position: relative;
		width: 100%;
		max-width: 1604px;
		display: grid;
		grid-template-columns: 1fr;
		grid-template-rows: 1fr;
	}

	.header-image {
		width: 100%;
		height: 24rem;
		object-fit: cover;
		object-position: center center;
		grid-column: 1 / -1;
		grid-row: 1 / -1;

		@media screen and (min-width: 640px) {
			height: 31.25rem;
		}
	}

	.cms-detail-header > :not(img) {
		grid-column: 1 / -1;
		grid-row: 1 / -1;
		z-index: 1;
	}
</style>
